import { isValidElement } from 'react';
import { resolveColumns } from './resolveColumns';
import resolveValuePath from '../../../Helpers/resolveValuePath';

/** The cells that read as a line of text, in the order a card would stack them. */
const LINE_CELLS = [
  undefined,
  null,
  '',
  'text',
  'title',
  'customerName',
  'phone',
];

/** The cells that read as a badge, most badge-like first. */
const BADGE_CELLS = ['status', 'iconValue', 'monthChip', 'flag'];

/** The entry a slot takes when the layout does not say, out of the ones no slot has claimed. */
const impliedSlots = (spare) => ({
  primary: spare.find(({ cell }) => LINE_CELLS.includes(cell)),
  secondary: spare.filter(({ cell }) => LINE_CELLS.includes(cell))[1],
  trailing: BADGE_CELLS.reduce(
    (found, badge) => found ?? spare.find(({ cell }) => cell === badge),
    undefined
  ),
});

/**
 * Compiles a column layout into the slots a card shows: the line it leads with, the line under it,
 * and the badge beside them.
 *
 * The same layout the table renders, read for a surface with room for three things rather than
 * eight. An entry says where it belongs with `slot`; a layout that says nothing is placed by order,
 * so one saved before cards existed still renders.
 *
 * Each slot is a compiled column - `{ id, label, formatCallback }` - so a card draws it through
 * `renderColumnValue`, exactly as a table row does.
 *
 * @param {Array} layout - The columns to show, in order.
 * @param {Object} options - Compilation options, as `resolveColumns` takes them.
 * @param {Object} options.context - Runtime values a saved layout cannot hold, read by cells.
 * @param {Object} options.renderers - Cells to add to the registry, or override in it.
 *
 * @returns {Object} `{ primary, secondary, trailing }`, each a compiled column or null.
 */
export const resolveCard = (layout, { context = {}, renderers = {} } = {}) => {
  const entries = (layout ?? []).filter(({ slot }) => slot !== 'hidden');
  const declared = (name) => entries.find(({ slot }) => slot === name);

  const chosen = {
    primary: declared('primary'),
    secondary: declared('secondary'),
    trailing: declared('trailing'),
  };

  const spare = entries.filter(
    (entry) => !entry.slot && !Object.values(chosen).includes(entry)
  );
  const implied = impliedSlots(spare);

  const compiled = Object.fromEntries(
    ['primary', 'secondary', 'trailing'].map((name) => {
      const entry = chosen[name] ?? implied[name];

      return [
        name,
        entry ? resolveColumns([entry], { context, renderers })[0] : null,
      ];
    })
  );

  return compiled;
};

/**
 * What a compiled column shows for a row: the cell's rendering of the value, or the value itself
 * where the column declares no cell.
 *
 * @param {Object} column - A compiled column.
 * @param {Object} data - The row.
 * @param {Function} translate - The react-intl accessor.
 * @param {Object} parameters - Values the page publishes, for the indexes a key reaches through.
 *
 * @returns {*}
 */
/** Whether React can draw this: a primitive, an element, or a list of either. */
const isDrawable = (value) => {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.every(isDrawable);
  if (typeof value !== 'object') return true;

  return isValidElement(value);
};

export const renderColumnValue = (column, data, translate, parameters = {}) => {
  const value = resolveValuePath(data, column?.id, parameters);
  const drawn = column?.formatCallback
    ? column.formatCallback(value, data, translate)
    : value;

  // A key may name any branch of the record - a contact, an address, the list of email addresses -
  // and the catalogue offers those alongside the values. Drawing one throws where it is rendered and
  // takes the whole page with it, so a value no cell has turned into something drawable reads as
  // nothing at all, and says so once rather than bringing the page down.
  if (isDrawable(drawn)) return drawn;

  console.warn(`Column ${column?.id} resolves to a value no cell draws`, drawn);

  return null;
};

export default resolveCard;
